import { DateValue } from '@internationalized/date';
import React, { ReactElement } from 'react';
import {
  OverlayArrow,
  Popover as AriaPopover,
  PopoverProps as AriaPopoverProps,
  composeRenderProps,
  PopoverContext,
  useSlottedContext,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

export interface PopoverProps extends Omit<AriaPopoverProps, 'children'> {
  showArrow?: boolean;
  children: React.ReactNode;
  previousRange: {
    startDate: DateValue;
    endDate: DateValue;
  } | null;
  handleCancelAndOutsideInteraction?: () => void;
}

const styles = tv({
  base: 'bg-white ',
  variants: {
    isEntering: {
      true: 'animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 placement-left:slide-in-from-right-1 placement-right:slide-in-from-left-1 ease-out duration-200',
    },
    isExiting: {
      true: 'animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 placement-left:slide-out-to-right-1 placement-right:slide-out-to-left-1 ease-in duration-150',
    },
  },
});

export const Popover = ({
  children,
  showArrow,
  className,
  handleCancelAndOutsideInteraction,
  ...props
}: PopoverProps): ReactElement => {
  const popoverContext = useSlottedContext(PopoverContext)!;
  const isSubmenu = popoverContext.trigger === 'SubmenuTrigger';
  let offset = showArrow ? 12 : 8;
  offset = isSubmenu ? offset - 6 : offset;
  return (
    <AriaPopover
      offset={offset}
      {...props}
      className={composeRenderProps(className, (cls, renderProps) =>
        styles({ ...renderProps, className: cls }),
      )}
      shouldCloseOnInteractOutside={() => {
        if (handleCancelAndOutsideInteraction) {
          handleCancelAndOutsideInteraction();
        }
        return true;
      }}
    >
      {showArrow && (
        <OverlayArrow className={'group'}>
          <svg
            width={12}
            height={12}
            viewBox={'0 0 12 12'}
            className={
              'block fill-white stroke-white stroke-1 group-placement-left:-rotate-90 group-placement-right:rotate-90 group-placement-bottom:rotate-180'
            }
            role={'img'}
            aria-hidden={'true'}
          >
            <path d={'M0 0 L6 6 L12 0'} />
          </svg>
        </OverlayArrow>
      )}
      {children}
    </AriaPopover>
  );
};
